/**
 * AircraftListPanel - Slide-out panel listing all aircraft on site
 *
 * Opens from the left edge (via shadcn Sheet). Shows a scrollable list
 * of aircraft with tail number, type, owner, area/unit, and contract badge.
 * Click a row to focus the camera on that aircraft.
 */

import { useMemo, useState } from 'react'
import { Plane, MapPin, Search, GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { useRampPanelStore } from '@/stores/rampPanelStore'
import { formatDate } from '@/lib/utils/format-business-data'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface AircraftListPanelProps {
  enrichedData: Map<string, AllocationEnrichedData>
  onFocusAircraft: (allocationId: string) => void
}

function getContractBadge(data: AllocationEnrichedData) {
  if (data.leaseContract) {
    return { label: 'Lease', variant: 'default' as const, className: 'bg-blue-600' }
  }
  if (data.transientStay) {
    return { label: 'Transient', variant: 'default' as const, className: 'bg-amber-600' }
  }
  return null
}

function getClientName(data: AllocationEnrichedData): string {
  const owner = data.aircraft.owner
  if (owner?.companyName) return owner.companyName
  if (owner?.firstName || owner?.lastName) {
    return [owner.firstName, owner.lastName].filter(Boolean).join(' ')
  }
  return data.aircraft.ownerName || '-'
}

export function AircraftListPanel({ enrichedData, onFocusAircraft }: AircraftListPanelProps) {
  const { activePanel, closePanel } = useRampPanelStore()
  const isOpen = activePanel === 'aircraft-list'
  const [search, setSearch] = useState('')
  const [toBePlacedOpen, setToBePlacedOpen] = useState(true)

  // Split into "To Be Placed" (reserved, no unit) and "On Site" (placed)
  const { toBePlaced, onSite } = useMemo(() => {
    const unplaced: AllocationEnrichedData[] = []
    const placed: AllocationEnrichedData[] = []

    for (const data of enrichedData.values()) {
      if (data.allocation.status === 'reserved' && !data.allocation.unitId) {
        unplaced.push(data)
      } else {
        placed.push(data)
      }
    }

    return { toBePlaced: unplaced, onSite: placed }
  }, [enrichedData])

  // Apply search filter to on-site list
  const filteredOnSite = useMemo(() => {
    if (!search.trim()) return onSite

    const q = search.toLowerCase()
    return onSite.filter((d) => {
      const tail = d.aircraft.tailNumber.toLowerCase()
      const client = getClientName(d).toLowerCase()
      const type = d.aircraft.typeCode?.toLowerCase() || ''
      return tail.includes(q) || client.includes(q) || type.includes(q)
    })
  }, [onSite, search])

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closePanel()}>
      <SheetContent side="left" className="w-[360px] p-0">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Plane className="w-4 h-4" />
            Aircraft on Site
            <Badge variant="secondary" className="ml-auto text-xs">
              {enrichedData.size}
            </Badge>
          </SheetTitle>
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tail, type, client..."
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-130px)]">
          {/* To Be Placed section */}
          {toBePlaced.length > 0 && (
            <Collapsible open={toBePlacedOpen} onOpenChange={setToBePlacedOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors">
                <GripVertical className="w-3.5 h-3.5" />
                To Be Placed
                <Badge variant="outline" className="ml-auto text-[10px] h-4 px-1 border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400">
                  {toBePlaced.length}
                </Badge>
                <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', toBePlacedOpen && 'rotate-180')} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="divide-y">
                  {toBePlaced.map((data) => {
                    const badge = getContractBadge(data)
                    const clientName = getClientName(data)
                    const startDate = data.leaseContract?.startDate || data.transientStay?.checkIn

                    return (
                      <div
                        key={data.allocationId}
                        className="w-full text-left px-4 py-3 bg-amber-50/50 dark:bg-amber-950/10"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                {data.aircraft.tailNumber}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {data.aircraft.typeCode}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {clientName}
                            </div>
                          </div>
                          {badge && (
                            <Badge className={cn('text-[10px] px-1.5', badge.className)}>
                              {badge.label}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          {startDate && (
                            <span className="text-[10px] text-muted-foreground">
                              Starts {formatDate(startDate)}
                            </span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[10px] ml-auto"
                            onClick={() => {
                              onFocusAircraft(data.allocationId)
                              closePanel()
                            }}
                          >
                            <GripVertical className="w-3 h-3 mr-1" />
                            Place on Map
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* On Site list */}
          <div className="divide-y">
            {filteredOnSite.map((data) => {
              const badge = getContractBadge(data)
              const clientName = getClientName(data)

              return (
                <button
                  key={data.allocationId}
                  className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors"
                  onClick={() => {
                    onFocusAircraft(data.allocationId)
                    closePanel()
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {data.aircraft.tailNumber}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {data.aircraft.typeCode}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {clientName}
                      </div>
                    </div>
                    {badge && (
                      <Badge className={cn('text-[10px] px-1.5', badge.className)}>
                        {badge.label}
                      </Badge>
                    )}
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" />
                    <span>
                      {data.allocation.areaName || 'Unassigned'}
                      {data.allocation.unitCode && ` · ${data.allocation.unitCode}`}
                    </span>
                  </div>
                </button>
              )
            })}

            {filteredOnSite.length === 0 && toBePlaced.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                {search ? 'No matching aircraft' : 'No aircraft on site'}
              </div>
            )}

            {filteredOnSite.length === 0 && toBePlaced.length > 0 && !search && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No aircraft placed on map yet
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
